import { MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES } from "./serviceCatalog";

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const CITY_BY_SLUG = new Map(MAJOR_INDIAN_CITIES.map((city) => [slugify(city), city]));
const CATEGORY_BY_SLUG = new Map(SERVICE_CATEGORIES.map((cat) => [slugify(cat.name), cat.name]));
const CATEGORY_QUERY_ALIASES = new Map([
  ["vet", "Veterinary & Health Care"],
  ["veterinary", "Veterinary & Health Care"],
  ["doctor", "Veterinary & Health Care"],
]);

export function buildLandingHash({ city, category }) {
  const citySlug = slugify(city);
  const categorySlug = slugify(category);
  if (!citySlug || !categorySlug) {
    return "#/";
  }
  return `#/services/${encodeURIComponent(citySlug)}/${encodeURIComponent(categorySlug)}`;
}

export function buildCategoryHash(category) {
  const categorySlug = slugify(category);
  if (!categorySlug) return "#/";
  return `#/category/${encodeURIComponent(categorySlug)}`;
}

export function buildPageHash(page) {
  const normalized = String(page || "").trim();
  if (!normalized || normalized === "browse") return "#/";
  return `#/page/${encodeURIComponent(normalized)}`;
}

// CONSULTATION CHAT: Build hash route for in-app consultation room.
export function buildConsultationHash(bookingId) {
  const normalized = String(bookingId || "").trim();
  if (!normalized) return "#/";
  return `#/consultation/${encodeURIComponent(normalized)}`;
}

export function parseLandingHash(hashValue) {
  const hash = String(hashValue || "");
  const match = hash.match(/^#\/services\/([^/]+)\/([^/]+)\/?$/i);
  if (match) {
    const citySlug = decodeURIComponent(match[1] || "").toLowerCase();
    const categorySlug = decodeURIComponent(match[2] || "").toLowerCase();
    const city = CITY_BY_SLUG.get(citySlug);
    const category = CATEGORY_BY_SLUG.get(categorySlug);
    if (!city || !category) return null;
    return { city, category };
  }

  const queryMatch = hash.match(/^#\/services\?(.*)$/i);
  if (queryMatch) {
    const params = new URLSearchParams(queryMatch[1] || "");
    const rawCity = String(params.get("city") || "").trim();
    const rawCategory = String(params.get("category") || "").trim();
    const city = CITY_BY_SLUG.get(slugify(rawCity));
    const categorySlug = slugify(rawCategory);
    const category = CATEGORY_BY_SLUG.get(categorySlug) || CATEGORY_QUERY_ALIASES.get(categorySlug);
    if (!city || !category) return null;
    return { city, category };
  }

  return null;
}

export function parseCategoryHash(hashValue) {
  const hash = String(hashValue || "");
  const match = hash.match(/^#\/category\/([^/]+)\/?$/i);
  if (!match) return null;
  const categorySlug = decodeURIComponent(match[1] || "").toLowerCase();
  const category = CATEGORY_BY_SLUG.get(categorySlug);
  if (!category) return null;
  return { category };
}

export function parsePageHash(hashValue) {
  const hash = String(hashValue || "");
  if (!hash) return null;
  if (hash === "#/" || hash === "#") {
    return { page: "browse" };
  }
  const match = hash.match(/^#\/page\/([^/]+)\/?$/i);
  if (!match) return null;
  const page = decodeURIComponent(match[1] || "").trim();
  if (!page) return { page: "browse" };
  return { page };
}

// CONSULTATION CHAT: Parse consultation hash into bookingId route params.
export function parseConsultationHash(hashValue) {
  const hash = String(hashValue || "");
  const match = hash.match(/^#\/consultation\/([^/]+)\/?$/i);
  if (!match) return null;
  const bookingId = decodeURIComponent(match[1] || "").trim();
  if (!bookingId) return null;
  return { page: "consultation", bookingId };
}
